import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Music, Calendar, ArrowRight, Sparkles, PartyPopper } from 'lucide-react'

export default function PartysKarneval() {
  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HEADER SECTION - Compact without Hero Image
      ════════════════════════════════════════════════════════════ */}
      <section className="relative pt-32 pb-16 border-b border-white/5">
        {/* Background Glow */}
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-brand-900/10 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          {/* Header Content */}
          <div className="flex flex-col items-center text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
                <PartyPopper className="w-3 h-3 text-brand-400" />
                <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Die fünfte Jahreszeit</span>
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light tracking-tight leading-[1.1] mb-6"
            >
              Partys & <span className="italic text-white/60">Karneval</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="text-white/50 text-base md:text-lg max-w-2xl leading-relaxed"
            >
              Feiern Sie mit uns die fünfte Jahreszeit und unvergessliche Partys in historischem Ambiente.
            </motion.p>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTENT SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-20 md:py-28">
        {/* Background Glows */}
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Music className="w-9 h-9 text-brand-400" />
            </div>
            
            <h2 className="font-serif text-2xl md:text-3xl text-white font-light mb-6">
              Karneval bei Hückels May
            </h2>
            
            <p className="text-white/50 text-lg max-w-3xl mx-auto leading-relaxed mb-8">
              Erleben Sie ausgelassene Stimmung, tolle Musik und unvergessliche Momente 
              in unserem historischen Ambiente. Ob Weiberfastnacht, Karnevalssamstag oder 
              Rosenmontag – bei uns wird gefeiert!
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/partys">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3"
                >
                  <Calendar className="w-4 h-4" />
                  Alle Events ansehen
                </motion.button>
              </Link>
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 border border-white/30 text-white text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-3"
                >
                  Anfrage stellen
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Sparkles className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Jetzt planen</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Ihre eigene <span className="italic text-white/60">Party</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Planen Sie Ihre private Feier oder Firmenparty in unserem einzigartigen Ambiente.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3 mx-auto"
              >
                Jetzt anfragen
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
